import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Building2, UserCheck, Award, ChevronRight, Lock,
  ArrowRight,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Lightweight, non-blocking click beacon for the Cover page. */
function trackCover(event) {
  try {
    const url = `${BACKEND}/api/public/cover-analytics`;
    const body = JSON.stringify({ event, referrer: document.referrer || null });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch (_e) { /* analytics must never break UX */ }
}

// User-supplied cinematic banner (logo + tagline + team photo baked in).
const HERO_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_secure-staff-hub/artifacts/z1u32z1a_ChatGPT%20Image%20Jul%2016%2C%202026%2C%2009_25_36%20AM.png";

/**
 * Cover — public splash for non-authenticated visitors.
 * V4.2: Displays the user's cinematic banner as the hero at full width,
 * followed by the three audience tracks. Dark navy + orange throughout.
 */
export default function Cover() {
  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-white overflow-hidden" data-testid="cover-page">
      {/* Minimal top bar (banner has its own logo, so we keep this ultra-subtle) */}
      <header className="absolute top-0 right-0 z-20 flex items-center justify-end px-6 sm:px-10 py-5 gap-3">
        <Link
          to="/login"
          className="text-xs sm:text-sm text-white/90 hover:text-white font-outfit font-bold uppercase tracking-widest bg-black/40 backdrop-blur border border-white/20 hover:border-[#FF7200] px-4 py-2 rounded-md transition"
          data-testid="cover-topbar-login"
          onClick={() => trackCover("topbar_signin")}
        >
          Sign in →
        </Link>
      </header>

      {/* Hero banner — user's cinematic image, full width, no overlay text */}
      <section className="relative w-full" data-testid="cover-hero-banner">
        <img
          src={HERO_IMG}
          alt="SecuSource · Deploy big. Deliver excellence. Elite teams. Trusted by professionals. Securing what matters."
          className="w-full h-auto block"
        />
        {/* Seamless fade into the dark section below */}
        <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(6,16,23,0) 0%, #0A0A0A 100%)" }} />
      </section>

      {/* Three audience doors */}
      <main className="relative">
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-4 pb-16">
          <div className="text-center mb-10">
            <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#FF7200] font-outfit font-bold">
              Choose your path
            </span>
            <h2 className="mt-3 font-outfit font-bold text-white tracking-tight"
                style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}>
              Three tracks. One professional standard.
            </h2>
            <div className="h-[2px] w-16 bg-[#FF7200] mx-auto mt-4" />
            <p className="mt-5 text-white/70 font-manrope max-w-2xl mx-auto text-sm sm:text-base">
              SecuSource segments the platform by audience so you only see what matters to you.
              Choose the track that fits your role.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3" data-testid="cover-audience-tracks">
            <TrackCard
              to="/employers"
              testId="cover-track-employers"
              trackLabel="Track 1"
              Icon={Building2}
              title="Employers"
              blurb="Security companies, event organisers, property managers and end-clients who need vetted personnel and cluster teams — fast."
              bullets={[
                "Source vetted guards, CPOs & teams",
                "3% Platform · 5% Managed · 10% Elite",
                "Full audit trail & deployment centre",
              ]}
              cta="Enter Employers"
              onClickTrack={() => trackCover("track_employers")}
            />
            <TrackCard
              to="/security-officers"
              testId="cover-track-officers"
              trackLabel="Track 2"
              Icon={UserCheck}
              title="Security Officers"
              blurb="Licensed guards, door staff, event stewards and site personnel who want fair rates, better opportunities and a portable professional profile."
              bullets={[
                "Free to join · No membership fee",
                "ERO-minimum rates or above",
                "Endorsements build your reputation",
              ]}
              cta="Enter Officers"
              onClickTrack={() => trackCover("track_officers")}
            />
            <TrackCard
              to="/elite-protection-network"
              testId="cover-track-elite"
              trackLabel="Track 3 · Elite"
              Icon={Award}
              title="Elite Protection Network"
              blurb="Close Protection Officers, executive security specialists and high-risk operators seeking verified, high-end deployments in the UK, Ireland and internationally."
              bullets={[
                "Global executive & CP opportunities",
                "Manually reviewed by protection pros",
                "Secure deployment rooms & audit trail",
              ]}
              cta="Enter Elite Network"
              featured
              onClickTrack={() => trackCover("track_elite")}
            />
          </div>

          {/* Secondary actions */}
          <div className="mt-10 flex flex-wrap justify-center items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 border border-white/25 hover:bg-white/10 text-white font-manrope font-semibold px-5 py-2.5 rounded-md text-sm"
              data-testid="cover-signin"
              onClick={() => trackCover("signin")}
            >
              <Lock className="w-4 h-4" /> Sign in to your account
            </Link>
            <Link
              to="/contact"
              className="text-sm text-white/60 hover:text-white font-manrope underline underline-offset-4"
              data-testid="cover-contact"
            >
              Contact us
            </Link>
          </div>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 max-w-4xl mx-auto">
            {[
              ["Verified", "PSA / SIA licence checks"],
              ["Private", "Anonymous profiles until booking"],
              ["Fair", "ERO minimum rates or above"],
              ["Compliant", "GDPR · Audit trail · Encrypted"],
            ].map(([t, d]) => (
              <div key={t} className="rounded-lg p-3 sm:p-4 bg-white/[0.04] border border-white/10 backdrop-blur">
                <div className="text-[10px] uppercase tracking-widest text-[#FF7200] font-outfit font-bold">{t}</div>
                <div className="text-xs sm:text-sm text-white/85 font-manrope mt-1">{d}</div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="relative border-t border-white/10 py-6 px-6 text-center text-xs text-white/50 font-manrope">
        © SecuSource · Access restricted · <Link to="/contact" className="hover:text-white underline">Contact</Link>
      </footer>
    </div>
  );
}

function TrackCard({ to, testId, trackLabel, Icon, title, blurb, bullets, cta, featured, onClickTrack }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      onClick={onClickTrack}
      className={`group relative rounded-2xl overflow-hidden text-left p-6 flex flex-col min-h-[340px] transition
        ${featured
          ? "border border-[#FF7200]/60 bg-gradient-to-br from-[#1a0f04] via-[#0f0a06] to-[#0A0A0A] hover:border-[#FF7200]"
          : "border border-white/10 bg-gradient-to-br from-[#141414] to-[#0A0A0A] hover:border-[#FF7200]/60"
        }`}
    >
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#FF7200]/15 blur-2xl group-hover:bg-[#FF7200]/30 transition" aria-hidden="true" />
      <div className="w-11 h-11 rounded-md flex items-center justify-center mb-4"
        style={{ background: "rgba(255,114,0,0.15)", border: "1px solid rgba(255,114,0,0.4)", color: "#FF7200" }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#FF7200] font-outfit font-bold mb-2">{trackLabel}</div>
      <div className="font-outfit font-bold text-white text-2xl leading-tight">{title}</div>
      <p className="text-sm text-white/75 font-manrope mt-3 flex-1">{blurb}</p>
      <ul className="mt-4 space-y-1.5 text-xs text-white/80 font-manrope">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-[#FF7200]" /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-5 inline-flex items-center gap-1.5 text-[#FF7200] font-outfit font-bold text-sm uppercase tracking-widest group-hover:gap-2.5 transition-all">
        {cta} {featured ? <ArrowRight className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </div>
    </Link>
  );
}
